import * as React from "react";
import {Bar, BarChart, CartesianGrid, Cell, Legend, ReferenceLine, Tooltip, XAxis, YAxis} from 'recharts';
import {format} from 'date-fns';
import {Payload} from "recharts/types/component/DefaultLegendContent";
import {LongRunningQueryByNs} from "../../../../../generated/api";
import {Button, Segment} from "semantic-ui-react";

interface ChartData {
    startTime: Date;
    duration: number;
    client: string;
    connectionId: number;
    op: string;
    ns: string;
    opid: number;
    isOutlier?: boolean;
}

// Color palette for different namespaces
const COLORS = [
    '#0088FE', '#00C49F', '#FFBB28', '#FF8042',
    '#8884d8', '#82ca9d', '#ffc658', '#8dd1e1',
    '#a4de6c', '#d0ed57', '#83a6ed', '#ff6b6b'
];

// Calculate smart Y-axis range
const calculateSmartYAxisDomain = (data: ChartData[], hideOutliers: boolean) => {
    if (data.length === 0) return {domain: [0, 100], hasOutliers: false, outlierThreshold: 0};

    const durations = data.map(d => d.duration).filter(d => d > 0);
    if (durations.length === 0) return {domain: [0, 100], hasOutliers: false, outlierThreshold: 0};

    // If we're not hiding outliers, show everything
    if (!hideOutliers) {
        return {
            domain: [0, Math.max(...durations) * 1.1],
            hasOutliers: false,
            outlierThreshold: Infinity
        };
    }

    // Sorted duration values
    const sorted = [...durations].sort((a, b) => a - b);

    // Calculate quartiles
    const q1 = sorted[Math.floor(sorted.length * 0.25)];
    const q3 = sorted[Math.floor(sorted.length * 0.75)];
    const iqr = q3 - q1;

    // Outlier threshold (1.5 * IQR rule)
    const outlierThreshold = q3 + (1.5 * iqr);

    // Data without extreme outliers for domain calculation
    const filteredDurations = durations.filter(d => d <= outlierThreshold);

    if (filteredDurations.length === 0) {
        return {
            domain: [0, Math.max(...durations) * 1.1],
            hasOutliers: false,
            outlierThreshold
        };
    }

    // Maximum value without outliers (with 10% buffer)
    const maxWithoutOutliers = Math.max(...filteredDurations) * 1.1;
    const hasOutliers = durations.some(d => d > outlierThreshold);

    return {
        domain: [0, maxWithoutOutliers],
        hasOutliers,
        outlierThreshold
    };
};

// Process data with outlier identification
const processData = (data: LongRunningQueryByNs[]): ChartData[] => {
    const chartData: ChartData[] = [];

    data.forEach((db) => {
        db.queries?.forEach((query) => {
            chartData.push({
                startTime: new Date(query.currentOpTime),
                duration: query.secs_running || 0,
                client: query.client?.split(':')[0] || 'unknown',
                connectionId: query.connectionId || 0,
                op: query.op || '',
                ns: query.ns || db.ns || 'unknown',
                opid: query.opid || 0
            });
        });
    });

    // Identify outliers
    if (chartData.length > 0) {
        const durations = chartData.map(d => d.duration);
        const sorted = [...durations].sort((a, b) => a - b);
        const q1 = sorted[Math.floor(sorted.length * 0.25)];
        const q3 = sorted[Math.floor(sorted.length * 0.75)];
        const iqr = q3 - q1;
        const outlierThreshold = q3 + (1.5 * iqr);

        chartData.forEach(item => {
            item.isOutlier = item.duration > outlierThreshold;
        });
    }

    // Sort by start time
    return chartData.sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
};

// Custom Tooltip component
const CustomTooltip: React.FC<any> = ({active, payload}) => {
    if (active && payload && payload.length) {
        const data = payload[0].payload;
        return (
            <div className="custom-tooltip bg-white p-3 border border-gray-300 rounded shadow-lg">
                <p className="font-bold">{format(data.startTime, 'HH:mm:ss')}</p>
                <p className="text-sm"><strong>Duration:</strong> {data.duration.toFixed(2)}s</p>
                {data.isOutlier && (
                    <p className="text-sm text-red-600 font-semibold">⚠️ Outlier</p>
                )}
                <p className="text-sm"><strong>Namespace:</strong> {data.ns}</p>
                <p className="text-sm"><strong>Client:</strong> {data.client}</p>
                <p className="text-sm"><strong>Operation:</strong> {data.op}</p>
            </div>
        );
    }
    return null;
};

// Main component
export const DatabaseCallsChart: React.FC<{ data: LongRunningQueryByNs[] }> = ({data}) => {
    const processedData = React.useMemo(() => processData(data), [data]);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const [dimensions, setDimensions] = React.useState({width: 800, height: 600});

    // State for toggling namespaces
    const [hiddenNamespaces, setHiddenNamespaces] = React.useState<Set<string>>(new Set());

    // State for toggling outlier hiding
    const [hideOutliers, setHideOutliers] = React.useState<boolean>(true);

    // Container size measurement
    React.useEffect(() => {
        const updateDimensions = () => {
            if (containerRef.current) {
                const {width} = containerRef.current.getBoundingClientRect();
                setDimensions({
                    width: width - 40,
                    height: 600 // Fixed height for chart
                });
            }
        };

        updateDimensions();
        window.addEventListener('resize', updateDimensions);

        return () => window.removeEventListener('resize', updateDimensions);
    }, []);

    // Calculate Y-axis domain based on hideOutliers setting
    const yAxisConfig = React.useMemo(() =>
            calculateSmartYAxisDomain(processedData, hideOutliers),
        [processedData, hideOutliers]
    );

    // Unique namespaces
    const uniqueNamespaces = React.useMemo(() => {
        const namespaces = processedData.map(item => item.ns);
        return [...new Set(namespaces)].filter(ns => ns).sort();
    }, [processedData]);

    // Color mapping
    const namespaceColorMap = React.useMemo(() => {
        const map: Record<string, string> = {};
        uniqueNamespaces.forEach((ns, index) => {
            map[ns] = COLORS[index % COLORS.length];
        });
        return map;
    }, [uniqueNamespaces]);

    // Filtered data based on hidden namespaces
    const filteredData = React.useMemo(() => {
        if (hiddenNamespaces.size === 0) return processedData;
        return processedData.filter(item => !hiddenNamespaces.has(item.ns));
    }, [processedData, hiddenNamespaces]);

    // Data for display (based on hideOutliers setting)
    const chartDataForDisplay = React.useMemo(() => {
        if (!hideOutliers) {
            return filteredData;
        }
        return filteredData.filter(d => d.duration <= yAxisConfig.domain[1]);
    }, [filteredData, hideOutliers, yAxisConfig.domain]);

    // Outlier statistics
    const outlierStats = React.useMemo(() => {
        const outliers = filteredData.filter(d => d.isOutlier);
        if (outliers.length === 0) return null;

        return {
            count: outliers.length,
            min: Math.min(...outliers.map(o => o.duration)),
            max: Math.max(...outliers.map(o => o.duration)),
            avg: outliers.reduce((sum, o) => sum + o.duration, 0) / outliers.length,
            namespaces: [...new Set(outliers.map(o => o.ns))]
        };
    }, [filteredData]);

    // Toggle namespace visibility
    const toggleNamespace = (namespace: string) => {
        setHiddenNamespaces(prev => {
            const newSet = new Set(prev);
            if (newSet.has(namespace)) {
                newSet.delete(namespace);
            } else {
                newSet.add(namespace);
            }
            return newSet;
        });
    };

    // Toggle all namespaces
    const toggleAllNamespaces = (showAll: boolean) => {
        if (showAll) {
            setHiddenNamespaces(new Set());
        } else {
            setHiddenNamespaces(new Set(uniqueNamespaces));
        }
    };

    // Toggle outlier hiding
    const toggleHideOutliers = () => {
        setHideOutliers(prev => !prev);
    };

    // Legend data
    const legendPayload = React.useMemo(() => {
        return uniqueNamespaces
            .filter(ns => !hiddenNamespaces.has(ns))
            .map(ns => ({
                value: ns,
                type: 'square',
                color: namespaceColorMap[ns]
            }));
    }, [uniqueNamespaces, namespaceColorMap, hiddenNamespaces]);

    if (processedData.length === 0) {
        return (
            <div className="w-full h-full p-4 flex items-center justify-center">
                <p className="text-gray-500">No data available</p>
            </div>
        );
    }

    return (
        <div
            ref={containerRef}
            className="w-full h-full p-4 overflow-auto"
            style={{minHeight: '600px'}}
        >
            <h2>Database Calls by Namespace</h2>

            {/* Control panel with outlier toggle */}
            <Segment size="mini">
                <div>
                    <h3>
                        Outlier Handling:
                    </h3>
                    <Button
                        toggle
                        active={!hideOutliers}
                        onClick={toggleHideOutliers}
                        style={{minWidth: '120px'}}
                    >
                        {hideOutliers ? 'Show All Data' : 'Hide Outliers'}
                    </Button>
                    <span>
                        {hideOutliers ? 'Outliers are hidden' : 'Showing all data'}
                    </span>
                </div>

                {hideOutliers && yAxisConfig.hasOutliers && (
                    <div>
                        ⚠️ {outlierStats?.count || 0} outliers hidden
                        (threshold: {yAxisConfig.outlierThreshold.toFixed(0)}s)
                    </div>
                )}
            </Segment>

            {/* Namespace toggle controls */}
            <div>
                <div>
                    <h3>
                        Namespace Filter:
                    </h3>
                    <Button
                        size="small"
                        onClick={() => toggleAllNamespaces(true)}
                    >
                        Show All Namespaces
                    </Button>
                </div>

                <div style={{
                    flex: 1,
                    flexDirection: 'row',
                    display: 'flex'
                }}>
                    {uniqueNamespaces.map((ns) => {
                        const isHidden = hiddenNamespaces.has(ns);
                        const nsColor = namespaceColorMap[ns];
                        const callCount = processedData.filter(d => d.ns === ns).length;

                        return (
                            <Button
                                key={ns}
                                size="small"
                                onClick={() => toggleNamespace(ns)}
                                title={`${ns} (${callCount} calls)`}
                                style={!isHidden ? {
                                    backgroundColor: nsColor,
                                    borderColor: nsColor,
                                    color: 'white'
                                } : {}}
                            >
                                <span className="font-medium max-w-[100px] truncate">{ns}</span>
                                <span className="ml-1 bg-white bg-opacity-20 px-1 rounded text-xs">
                                    {callCount}
                                </span>
                            </Button>
                        );
                    })}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                    {uniqueNamespaces.length - hiddenNamespaces.size} of {uniqueNamespaces.length} namespaces visible
                </p>
            </div>

            {/* Chart container with fixed height */}
            <div className="mb-6" style={{width: '100%', height: '600px'}}>
                <BarChart
                    width={dimensions.width}
                    height={600}
                    data={chartDataForDisplay}
                    margin={{top: 20, right: 30, left: 20, bottom: 60}}
                >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>

                    <XAxis
                        dataKey="startTime"
                        tickFormatter={(value: Date) => format(value, 'dd.MM.yyyy HH:mm:ss')}
                        angle={-45}
                        textAnchor="end"
                        height={60}
                        label={{
                            value: 'Call Start Time',
                            position: 'insideBottom',
                            offset: -10
                        }}
                    />

                    <YAxis
                        domain={yAxisConfig.domain}
                        label={{
                            value: 'Elapsed Duration (s)',
                            angle: -90,
                            position: 'insideLeft',
                            offset: 10
                        }}
                        tickFormatter={(value: number) => `${value}s`}
                        allowDataOverflow={true}
                    />

                    {hideOutliers && yAxisConfig.hasOutliers && (
                        <ReferenceLine
                            y={yAxisConfig.domain[1]}
                            stroke="#ff6b6b"
                            strokeDasharray="3 3"
                            label={{
                                value: 'Outlier Threshold',
                                position: 'insideRight',
                                fill: '#ff6b6b'
                            }}
                        />
                    )}

                    <Tooltip content={<CustomTooltip/>}/>

                    <Legend
                        payload={legendPayload as Payload[]}
                        verticalAlign="top"
                        height={36}
                        wrapperStyle={{paddingBottom: '10px'}}
                    />

                    <Bar
                        dataKey="duration"
                        name="Duration"
                        radius={[2, 2, 0, 0]}
                        isAnimationActive={false}
                    >
                        {chartDataForDisplay.map((entry, index) => (
                            <Cell
                                key={`cell-${index}`}
                                fill={namespaceColorMap[entry.ns] || '#CCCCCC'}
                                stroke={entry.isOutlier ? '#ff0000' : undefined}
                                strokeWidth={entry.isOutlier ? 2 : 0}
                            />
                        ))}
                    </Bar>
                </BarChart>
            </div>

            {/* Statistics section */}
            <div className="mt-6 p-4 bg-gray-50 rounded border">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                        <h3 className="font-bold text-gray-700 mb-2">Overall Statistics</h3>
                        <div className="space-y-1 text-sm">
                            <p><span className="font-medium">Total Calls:</span> {filteredData.length}</p>
                            <p><span className="font-medium">Visible Calls:</span> {chartDataForDisplay.length}</p>
                            <p><span
                                className="font-medium">Visible Namespaces:</span> {uniqueNamespaces.length - hiddenNamespaces.size}
                            </p>
                            <p><span className="font-medium">Y-axis Range:</span> 0 - {yAxisConfig.domain[1].toFixed(0)}s
                            </p>
                            <p>
                                <span className="font-medium">Outlier Handling:</span>
                                <span
                                    className={`ml-2 font-medium ${hideOutliers ? 'text-yellow-700' : 'text-green-700'}`}>
                                    {hideOutliers ? 'Outliers Hidden' : 'Showing All Data'}
                                </span>
                            </p>
                        </div>
                    </div>

                    {outlierStats && (
                        <div className="md:col-span-2">
                            <h3 className="font-bold text-gray-700 mb-2">
                                Outlier Statistics {!hideOutliers && '(Visible in Chart)'}
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                                <div className="space-y-1">
                                    <p><span className="font-medium">Total Outliers:</span> {outlierStats.count}</p>
                                    <p><span className="font-medium">Min Duration:</span> {outlierStats.min.toFixed(0)}s
                                    </p>
                                    <p><span className="font-medium">Max Duration:</span> {outlierStats.max.toFixed(0)}s
                                    </p>
                                </div>
                                <div className="space-y-1">
                                    <p><span
                                        className="font-medium">Average Duration:</span> {outlierStats.avg.toFixed(0)}s
                                    </p>
                                    <p><span
                                        className="font-medium">Threshold:</span> {yAxisConfig.outlierThreshold.toFixed(0)}s
                                    </p>
                                    <p><span
                                        className="font-medium">Affected Namespaces:</span> {outlierStats.namespaces.length}
                                    </p>
                                </div>
                                <div className="space-y-1">
                                    <p className="font-medium">Outlier Namespaces:</p>
                                    <div className="text-xs max-h-20 overflow-y-auto">
                                        {outlierStats.namespaces.map(ns => (
                                            <div key={ns} className="flex items-center gap-1 mb-1">
                                                <div
                                                    className="w-3 h-3 rounded"
                                                    style={{backgroundColor: namespaceColorMap[ns]}}
                                                />
                                                <span className="truncate">{ns}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            {!hideOutliers && (
                                <div
                                    className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-sm text-blue-700">
                                    ℹ️ Outliers are currently visible in the chart (red borders)
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};